import SelectBuscable from '@/shared/components/SelectBuscable'
import { TituloDePantalla } from '@/shared/components/TituloDePantalla'
import React, { useState, useEffect } from "react";
import { ArrowLeft, Calendar, Save } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { apiService } from '@/services/api';
import { useAuth } from '@/shared/hooks/useAuth';
import { userHasPermission } from '@/shared/lib/roleUtils';
import PermissionsDenyPage from '@/pages/configuracion/roles/permissionsDenyPage';
import DatosDeEjemploBanner from '@/shared/components/DatosDeEjemploBanner';
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton';

interface CriteriaScore {
  id: number;
  name: string;
  score: number;
}

/**
 * Catálogo de usuarios para el select del evaluador.
 *
 * Se lee de `/users/public/all`, no del listado de usuarios: ese pide el permiso
 * de administración y quien captura una evaluación no tiene por qué tenerlo. El
 * público solo pide sesión iniciada y ya lo usan los formularios de Almacén.
 */
interface UsuarioOpcion {
  id: string;
  firstName: string | null;
  lastName: string | null;
  puesto?: string | null;
}

const nombreDe = (u: UsuarioOpcion) =>
  [u.firstName, u.lastName].filter(Boolean).join(' ').trim() || 'Sin nombre';

export const CreateEvaluation: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [supplier, setSupplier] = useState("");
  const [period, setPeriod] = useState("");
  const [project, setProject] = useState("");
  const [evaluator, setEvaluator] = useState("");
  const [evaluationDate, setEvaluationDate] = useState("");
  const [generalComments, setGeneralComments] = useState("");
  const [usuarios, setUsuarios] = useState<UsuarioOpcion[]>([]);
  const [cargandoUsuarios, setCargandoUsuarios] = useState(true);
  const [errorUsuarios, setErrorUsuarios] = useState(false);

  useEffect(() => {
    let vigente = true;
    apiService
      .request<UsuarioOpcion[]>("/users/public/all")
      .then((lista) => {
        if (vigente) setUsuarios(Array.isArray(lista) ? lista : []);
      })
      .catch(() => {
        // Se avisa en vez de dejar el select vacío como si no hubiera usuarios.
        if (vigente) setErrorUsuarios(true);
      })
      .finally(() => {
        if (vigente) setCargandoUsuarios(false);
      });
    return () => {
      vigente = false;
    };
  }, []);

  const [criteria, setCriteria] = useState<CriteriaScore[]>([
    { id: 1, name: "Entregas en tiempo", score: 0 },
    { id: 2, name: "Calidad del servicio / producto", score: 0 },
    { id: 3, name: "Cumplimiento documental", score: 0 },
    { id: 4, name: "Comunicación y respuesta", score: 0 },
    { id: 5, name: "Incidencias y reportes", score: 0 },
  ]);

  const handleScoreChange = (criteriaId: number, score: number) => {
    setCriteria((prev) =>
      prev.map((c) => (c.id === criteriaId ? { ...c, score } : c)),
    );
  };

  const calculateFinalRating = () => {
    const total = criteria.reduce((sum, c) => sum + c.score, 0);
    return criteria.length > 0 ? (total / criteria.length).toFixed(1) : "0.0";
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Aquí iría la lógica para guardar la evaluación
    navigate("/rh/evaluations");
  };

  // ISO (yyyy-mm-dd) del input date nativo → dd/mm/aaaa para mostrar en el overlay.
  const formatDateDDMMYYYY = (iso: string) => {
    const [y, m, d] = (iso || "").split("-");
    if (!y || !m || !d) return "";
    return `${d}/${m}/${y}`;
  };

  if (
    userHasPermission(
      "proveedores.fiscal.validaciones.consultar",
      user?.rbac?.effectivePermissions || [],
    ) === false
  ) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <PermissionsDenyPage />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      <DatosDeEjemploBanner detalle="El módulo de evaluaciones todavía no está conectado al servidor: lo que se capture aquí no se guarda." />
      {/* Header */}
      <div className="mb-6 flex gap-3">
        <button
          onClick={() => navigate("/rh/evaluations")}
          className={estiloDeBoton('secundario', 'mt-2 mb-3')}
        >
          <ArrowLeft className="w-4 h-4" />
          Volver
        </button>
        <TituloDePantalla nivel="interna">
          Nueva evaluación
        </TituloDePantalla>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-[#E0E0E0] mb-6">
          <div className="border-b border-[#E5E7EB] p-6">
            <p className="text-lg font-semibold text-[#1E1E1F]">
              Seleccionar Proveedor
            </p>
          </div>
          {/* Proveedor */}
          <div className="p-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Proveedor<span className="text-red-500">*</span>
            </label>
            <div className="relative xs:max-w-[300px] max-w-[550px]">
              <SelectBuscable
                variante="formulario"
                value={supplier}
                onChange={setSupplier}
                required
                placeholder="Selecciona al proveedor"
                placeholderDeBusqueda="Buscar proveedor…"
                className="xs:min-w-[300px] min-w-[550px]"
                opciones={[
                  { value: "proveedor1", label: "Proveedor 1" },
                  { value: "proveedor2", label: "Proveedor 2" },
                ]}
              />
            </div>
          </div>

          {/* Periodo Evaluado */}
          <div className="bg-white">
            <div className="border-b border-[#E5E7EB] p-6">
              <p className="text-lg font-semibold text-[#1E1E1F]">
                Periodo evaluado
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-6 py-10">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Mes/año
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={period}
                    onChange={(e) => setPeriod(e.target.value)}
                    placeholder="mm/aaaa"
                    className="w-full px-3 py-2 pr-10 border border-[#E5E7EB] rounded-lg bg-white  text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-blue-500" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Proyecto
                </label>
                <div className="relative">
                  <SelectBuscable
                    variante="formulario"
                    value={project}
                    onChange={setProject}
                    placeholder="Seleccionar"
                    placeholderDeBusqueda="Buscar proyecto…"
                    opciones={[
                      { value: "proyecto1", label: "Proyecto 1" },
                      { value: "proyecto2", label: "Proyecto 2" },
                    ]}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Evaluador y Fecha */}
          <div className="bg-white p-6 py-10 border-t border-[#E5E7EB]">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Evaluador
                </label>
                <div className="relative">
                  <SelectBuscable
                    variante="formulario"
                    value={evaluator}
                    onChange={setEvaluator}
                    disabled={cargandoUsuarios || errorUsuarios}
                    placeholder={
                      cargandoUsuarios
                        ? "Cargando usuarios…"
                        : errorUsuarios
                          ? "No se pudieron cargar los usuarios"
                          : "Selecciona al evaluador"
                    }
                    placeholderDeBusqueda="Buscar evaluador…"
                    opciones={usuarios.map((u) => ({
                      value: String(u.id),
                      label: u.puesto ? `${nombreDe(u)} — ${u.puesto}` : nombreDe(u),
                    }))}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Fecha de evaluación
                </label>
                <div className="relative h-[42px] border border-gray-300 rounded-lg bg-white focus-within:ring-2 focus-within:ring-blue-500">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm pointer-events-none select-none">
                    {evaluationDate ? (
                      <span className="text-gray-900">{formatDateDDMMYYYY(evaluationDate)}</span>
                    ) : (
                      <span className="text-gray-400">dd/mm/aaaa</span>
                    )}
                  </span>
                  <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-blue-500 pointer-events-none" />
                  <input
                    type="date"
                    value={evaluationDate}
                    onChange={(e) => setEvaluationDate(e.target.value)}
                    onClick={(e) =>
                      (
                        e.currentTarget as HTMLInputElement & {
                          showPicker?: () => void;
                        }
                      ).showPicker?.()
                    }
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Criterios de Evaluación */}
          <div className="bg-white">
            <div className="border-b border-[#E5E7EB] p-6">
              <p className="text-lg font-semibold text-[#1E1E1F]">
                Criterios de evaluación
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                Califica del 1 al 5 según tu consideración
              </p>
            </div>

            <div className="space-y-8 p-6 py-10">
              {criteria.map((criterion) => (
                <div
                  key={criterion.id}
                  className="w-full flex flex-row gap-3 items-center flex-wrap"
                >
                  <label className="w-1/2 block text-base font-medium text-gray-900 dark:text-white mb-4">
                    {criterion.id}. {criterion.name}
                  </label>
                  <div className="flex gap-3 justify-center">
                    {[1, 2, 3, 4, 5].map((score) => (
                      <button
                        key={score}
                        type="button"
                        onClick={() => handleScoreChange(criterion.id, score)}
                        className={`w-[64px] h-[43px] rounded-lg font-semibold text-lg transition-all ${
                          criterion.score === score
                            ? "bg-[#253579] text-white shadow-md"
                            : "bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
                        }`}
                      >
                        {score}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className=" p-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Comentarios generales
              </label>
              <textarea
                value={generalComments}
                onChange={(e) => setGeneralComments(e.target.value)}
                rows={4}
                placeholder="Ingresa aquí tus observaciones y comentarios"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-[#F6F9FB] dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              />
            </div>
          </div>

          {/* Calificación Final */}
          <div className="bg-white">
            <div className="border-b border-[#E5E7EB] p-6">
              <p className="text-lg font-semibold text-[#1E1E1F]">
                Calificación final
              </p>
            </div>
            <div className="bg-[#E8F8FD] rounded-lg p-6 m-6 mb-10">
              <p className="text-3xl font-bold text-[#116583]">
                {calculateFinalRating()}
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate("/rh/evaluations")}
            className={estiloDeBoton('secundario')}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className={estiloDeBoton('primario')}
          >
            <Save className="w-4 h-4" />
            Guardar evaluación
          </button>
        </div>
      </form>
    </div>
  );
};
